/**
 * Keno: an 80-number pool, one 20-number draw, and a closed registry of
 * paytables. No cards, no hidden information, no player decisions after the
 * window closes — the whole game is "which spots did my tickets catch, what
 * does that pay", so the engine is pure functions over one draw.
 *
 * No room, no sockets: the server layer supplies the draw (from its queued
 * stack in tests, else `room.rng`) and the rules.
 */
import type { KenoPaytableName, KenoRules } from './kenoRules.js';
import type { Rng } from './blackjack.js';

/** The board: spots are 1..KENO_POOL. */
export const KENO_POOL = 80;

/** How many numbers the house draws each round. */
export const KENO_DRAW_SIZE = 20;

/**
 * How many spots one ticket may pick. Real cards stop at 10 or 15; the
 * paytables below are pinned for exactly 1..10, so this is a ceiling the
 * rules clamp under, not a rule the host can raise.
 */
export const KENO_MAX_SPOTS = 10;

// --- the paytable registry ---------------------------------------------------

/**
 * Total-return multiples per chip staked, indexed `[spots][catches]`. A 0 is
 * a loss; the stake itself is *not* added back on top (roulette's registry
 * prices odds, this one prices returns — keno cards have always been printed
 * as "for 1"). Multipliers are integers, so any whole-chip stake pays whole
 * chips. Each row is pinned cell by cell in tier 0, and every row's house
 * edge is asserted strictly inside (0%, 50%) from the hypergeometric law —
 * a slate that pays players more than it takes is a bug, not a vibe.
 */
export type KenoPaytable = Readonly<Record<number, readonly number[]>>;

export const KENO_PAYTABLES: Record<KenoPaytableName, KenoPaytable> = {
  classic: {
    1: [0, 3],
    2: [0, 0, 12],
    3: [0, 0, 1, 37],
    4: [0, 0, 1, 3, 113],
    5: [0, 0, 0, 1, 13, 670],
    6: [0, 0, 0, 1, 4, 90, 1560],
    7: [0, 0, 0, 0, 3, 24, 310, 6400],
    8: [0, 0, 0, 0, 1, 8, 85, 1300, 8900],
    9: [0, 0, 0, 0, 1, 5, 25, 254, 4270, 10680],
    10: [3, 0, 0, 0, 0, 2, 17, 105, 586, 2930, 23800],
  },
  party: {
    1: [0, 3],
    2: [0, 1, 9],
    3: [0, 0, 2, 43],
    4: [0, 0, 2, 4, 95],
    5: [0, 0, 1, 2, 11, 483],
    6: [0, 0, 1, 2, 4, 57, 750],
    7: [0, 0, 0, 2, 4, 16, 184, 2990],
    8: [0, 0, 0, 1, 2, 6, 53, 747, 4390],
    9: [0, 0, 0, 1, 2, 4, 12, 101, 1541, 5285],
    10: [3, 0, 0, 0, 2, 4, 9, 49, 296, 1478, 9850],
  },
  longshot: {
    1: [0, 3],
    2: [0, 0, 12],
    3: [0, 0, 1, 40],
    4: [0, 0, 1, 3, 135],
    5: [0, 0, 0, 1, 11, 740],
    6: [0, 0, 0, 1, 3, 92, 1720],
    7: [0, 0, 0, 0, 2, 21, 335, 7590],
    8: [0, 0, 0, 0, 1, 7, 85, 1450, 9890],
    9: [0, 0, 0, 0, 1, 3, 21, 277, 5114, 16074],
    10: [3, 0, 0, 0, 0, 1, 16, 128, 764, 3863, 33800],
  },
};

export const kenoPaytable = (name: KenoPaytableName): KenoPaytable => KENO_PAYTABLES[name];

/**
 * What a ticket of `spots` picks returns per chip staked when `catches` of
 * its numbers come in. Anything off the card — more picks than the slate
 * prints, more catches than picks, a name the registry does not have — is a 0.
 */
export function kenoMultiplier(
  name: KenoPaytableName,
  spots: number,
  catches: number,
): number {
  return KENO_PAYTABLES[name]?.[spots]?.[catches] ?? 0;
}

// --- the draw ----------------------------------------------------------------

/**
 * Twenty distinct numbers, in draw order. A partial Fisher-Yates over the
 * pool, so the balls come out exactly as a machine would spit them — the TV
 * reveals them left to right in this order.
 */
export function drawKeno(rng: Rng): number[] {
  const pool = Array.from({ length: KENO_POOL }, (_, i) => i + 1);
  const draw: number[] = [];
  for (let i = 0; i < KENO_DRAW_SIZE; i += 1) {
    // Pick from the unshuffled tail and swap forward, so the picked prefix is
    // the draw and the untouched tail stays available.
    const j = i + rng(KENO_POOL - i);
    const picked = pool[j]!;
    pool[j] = pool[i]!;
    pool[i] = picked;
    draw.push(picked);
  }
  return draw;
}

/** The picked numbers the draw hit, ascending — what the phone highlights. */
export function kenoCatches(spots: readonly number[], draw: readonly number[]): number[] {
  const drawn = new Set(draw);
  return [...spots].filter((spot) => drawn.has(spot)).sort((a, b) => a - b);
}

/**
 * Whole chips returned to one ticket, stake included — `amount × multiplier`,
 * so a 0 is a clean sweep and the stake never rides on top. Fractional
 * multipliers cannot exist (the registry is integer-pinned), so a whole-chip
 * stake always pays whole chips.
 */
export function settleKenoTicket(
  spots: readonly number[],
  amount: number,
  draw: readonly number[],
  rules: Pick<KenoRules, 'paytable'>,
): { catches: number[]; payout: number } {
  const catches = kenoCatches(spots, draw);
  const multiplier = kenoMultiplier(rules.paytable, spots.length, catches.length);
  return { catches, payout: amount * multiplier };
}

// --- guarded ticket parse ----------------------------------------------------

/**
 * True when `spots` is exactly what a ticket may carry: integers 1–80,
 * distinct, no more than the table's cap and never past the card. The server
 * runs every incoming ticket through this before the numbers reach state,
 * the wire, or the paytable.
 */
export function isValidKenoTicket(spots: unknown, maxSpots: number): spots is number[] {
  if (!Array.isArray(spots)) return false;
  if (spots.length < 1 || spots.length > Math.min(maxSpots, KENO_MAX_SPOTS)) return false;
  const seen = new Set<number>();
  for (const spot of spots) {
    if (typeof spot !== 'number' || !Number.isInteger(spot) || spot < 1 || spot > KENO_POOL) {
      return false;
    }
    if (seen.has(spot)) return false;
    seen.add(spot);
  }
  return true;
}

/**
 * How a ticket reads on a phone or a TV: "6-spot · 3 12 24 41 55 70". The
 * spots print ascending regardless of the order they were tapped.
 */
export function describeKenoTicket(spots: readonly number[]): string {
  const listed = [...spots].sort((a, b) => a - b).join(' ');
  return `${spots.length}-spot · ${listed}`;
}
